<template>
  <div class="table-wrapper jobs-page">
    <div class="d-none d-lg-flex justify-content-between flex-wrap">
      <div class="jobs-main">
        <b-button
          v-if="$can('create', 'jobs', 'encoder')"
          variant="bg-primary"
          :to="{ name: 'jobs-create' }"
          class="mb-sm-0 mb-1"
        >
          <GjIcon
            class="icon-left"
            name="Plus"
          />
          Create
        </b-button>
        <b-button
          v-if="$can('get', 'jobs', 'encoder')"
          variant="bg-white"
          :disabled="!isSelected"
          :to="{
            name: 'jobs-summary',
            params: { name: selected.length > 0 ? selected[0].name : '' },
          }"
          class="mb-sm-0 mb-1"
        >
          View Details
        </b-button>
        <b-button
          v-if="$can('create', 'jobs', 'encoder')"
          variant="bg-white"
          :disabled="!isSelected"
          :to="{
            name: 'clone-job',
            params: { name: selected.length > 0 ? selected[0].name : '' },
          }"
          class="mb-sm-0 mb-1"
        >
          Clone
        </b-button>
        <b-button
          variant="bg-white"
          class="mr-1 mb-sm-0 mb-1"
          :disabled="!isSelected || !selected[0].isCancelable"
          @click="selected[0] && selected[0].isCancelable ? cancelJob(selected[0]) : ''"
        >
          Cancel
        </b-button>
      </div>
      <div>
        <b-button
          v-if="$can('list', 'jobs', 'encoder')"
          variant="bg-white"
          class="mr-0"
          @click="fetchJobs"
        >
          <GjIcon
            class="icon-left"
            name="Refresh"
          />
          Refresh
        </b-button>
      </div>
    </div>

    <div
      v-if="$can('create', 'jobs', 'encoder')"
      class="d-lg-none jobs-page__mobile-actions"
    >
      <b-button
        variant="bg-primary"
        block
        :to="{ name: 'jobs-create' }"
      >
        <GjIcon
          class="icon-left"
          name="Plus"
        />
        Create job
      </b-button>
    </div>

    <div :class="{ 'default-table': !isMobile }">
      <div class="table-actions mt-2 mb-1 mb-lg-0 d-none d-lg-block">
        <b-row
          v-if="!fetchingJobStatuses"
          align-h="between"
        >
          <b-col>
            <b-dropdown
              id="jobs-filter-dropdown-1"
              :text="jobStatusText"
              variant="bg-white"
              class="dropdown-icon-wrapper"
            >
              <template #button-content>
                <span>{{ jobStatusText }}</span>
                <GjIcon
                  class="icon-right"
                  name="ArrowDown"
                />
              </template>
              <b-dropdown-item
                v-for="status in jobStatusOptions"
                :key="status.id"
                @click="jobStatus = status.id"
              >
                {{ status.value }}
              </b-dropdown-item>
            </b-dropdown>
          </b-col>

          <b-input-group
            v-b-tooltip.hover.v-primary
            class="searchInputGroup mt-1 mt-sm-0"
            title="Search by job id, file path or tag"
          >
            <b-input-group-prepend is-text>
              <b-icon-search @click="requestJobs" />
            </b-input-group-prepend>
            <b-form-input
              id="filterInput"
              v-model.trim="searchInput"
              type="search"
              class="searchInput"
              placeholder="Search..."
              @change="requestJobs"
            />
          </b-input-group>
        </b-row>
        <b-row
          v-else
          class="justify-content-center"
        >
          <div style="height:42px" />
        </b-row>
      </div>

      <jobs-mobile-filters
        class="d-lg-none"
        :value="searchInput"
        :status="jobStatus"
        :status-options="jobStatusOptions"
        :all-statuses="allStatuses"
        :loading="fetchingJobs"
        @input="onMobileSearch"
        @update:status="jobStatus = $event"
        @refresh="fetchJobs"
      />

      <div class="position-relative d-none d-lg-block">
        <b-table
          ref="table"
          :select-mode="selectMode"
          :items="jobs"
          :fields="fields"
          :class="{ loaded: !fetchingJobs }"
          show-empty
          selectable
          responsive
          :busy="fetchingJobs"
          @row-selected="onRowSelected"
        >
          <template #cell(selected)="{ rowSelected }">
            <select-button :selected="rowSelected" />
          </template>

          <template #cell(name)="data">
            <router-link
              :to="{ name: 'jobs-summary', params: { name: data.value } }"
            >
              {{ data.value }}
            </router-link>
          </template>

          <template #cell(filePath)="data">
            {{ data.value }}
          </template>
          <template #cell(insertDate)="data">
            {{ formatDateTime(data.value) }}
          </template>
          <template #cell(endDateTime)="data">
            {{ formatDateTime(data.value) }}
          </template>
          <template #cell(status)="data">
            {{ data.value }}
          </template>

          <template #empty>
            <template v-if="fetchingJobs">
              <div class="loading-table text-center py-3">
                <b-spinner variant="primary" />
              </div>
            </template>
            <template v-else>
              <no-data
                no-data-text="No jobs have been added yet..."
                create-text="+ Create a job"
                access-page="Jobs"
                icon="Section"
                :has-permissions="hasPermissions"
                @action="$router.push({ name: 'jobs-create' })"
              />
            </template>
          </template>
        </b-table>
        <table-busy v-if="fetchingJobs && jobs.length > 0" />
      </div>

      <div class="d-lg-none jobs-page__mobile-list">
        <template v-if="fetchingJobs && jobs.length === 0">
          <job-mobile-card-skeleton :count="4" />
        </template>

        <template v-else-if="jobs.length > 0">
          <div class="jobs-page__count">
            <span>{{ countLabel }}</span>
            <b-spinner
              v-if="fetchingJobs"
              small
              variant="primary"
            />
          </div>
          <div :class="{ 'opacity-7': fetchingJobs }">
            <job-mobile-card
              v-for="job in jobs"
              :key="job.name"
              :job="job"
              class="top-fade-in"
              @cancel="cancelJob"
            />
          </div>
          <mobile-pagination
            v-model="currentPage"
            :total-rows="totalCount"
            :per-page="pageSize"
          />
        </template>

        <template v-else>
          <paper-simple class="top-fade-in">
            <no-data
              v-if="isFiltered"
              no-data-text="No jobs match your filters..."
              create-text="Clear filters"
              class="text-center"
              access-page="Jobs"
              icon="Search"
              :has-permissions="hasPermissions"
              @action="clearFilters"
            />
            <no-data
              v-else
              no-data-text="No jobs have been added yet..."
              create-text="+ Create a job"
              class="text-center"
              access-page="Jobs"
              icon="Section"
              :has-permissions="hasPermissions"
              @action="$router.push({ name: 'jobs-create' })"
            />
          </paper-simple>
        </template>
      </div>

      <div
        v-if="jobs.length > 0"
        class="default-pagination d-none d-lg-flex"
      >
        <b-pagination
          v-model="currentPage"
          :total-rows="totalCount"
          :per-page="pageSize"
          first-number
          last-number
        />
        <div class="results">
          Viewing {{ first.toLocaleString() }} to {{ last.toLocaleString() }} of {{ totalCount.toLocaleString() }} Items
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { BIconSearch } from 'bootstrap-vue'
import debounce from 'lodash/debounce'
import MobilePagination from '@/components/table/MobilePagination.vue'
import JobMobileCard from './components/JobMobileCard.vue'
import JobMobileCardSkeleton from './components/JobMobileCardSkeleton.vue'
import JobsMobileFilters from './components/JobsMobileFilters.vue'

const ALL_STATUSES = 0

export default {
  name: 'Jobs',
  components: {
    BIconSearch,
    MobilePagination,
    JobMobileCard,
    JobMobileCardSkeleton,
    JobsMobileFilters,
  },
  data() {
    return {
      selectMode: 'single',
      searchInput: '',
      jobStatus: ALL_STATUSES,
      fields: [
        {
          key: 'selected',
          label: '',
          variant: 'select',
          hide: true,
        },
        {
          key: 'name',
          label: 'Job ID',
          variant: 'guid',
          isPrimary: true,
        },
        {
          key: 'filePath',
          label: 'Input file path',
          variant: 'fill',
          isPrimary: true,
        },
        { key: 'insertDate', label: 'Submit time', variant: 'time' },
        { key: 'endDateTime', label: 'Finish time', variant: 'time' },
        { key: 'status', label: 'Status', variant: 'small' },
      ],
      selected: [],
      currentPage: 1,
      hasPermissions: true,
    }
  },
  computed: {
    isSelected() {
      if (this.selected[0]) {
        return true
      }
      return false
    },
    resultInfo() {
      return this.$store.state.jobs.jobsResultInfo
    },
    jobs() {
      return this.$store.state.jobs.jobs
    },
    fetchingJobs() {
      return this.$store.state.jobs.fetchingJobs
    },
    fetchingJobStatuses() {
      return this.$store.state.jobs.fetchingJobStatuses
    },
    jobStatusOptions() {
      return this.$store.state.jobs.jobStatuses || []
    },
    allStatuses() {
      return ALL_STATUSES
    },
    jobStatusText() {
      return (
        this.jobStatusOptions.find(j => j.id === this.jobStatus)?.value || ''
      )
    },
    isFiltered() {
      return this.searchInput !== '' || this.jobStatus !== ALL_STATUSES
    },
    totalCount() {
      return this.resultInfo?.totalCount || 0
    },
    pageSize() {
      return this.resultInfo?.pageSize || 10
    },
    pageIndex() {
      return this.currentPage - 1
    },
    first() {
      return this.pageIndex * this.pageSize + 1
    },
    last() {
      return this.currentPage * this.pageSize < this.totalCount
        ? this.currentPage * this.pageSize
        : this.totalCount
    },
    countLabel() {
      const total = this.totalCount || 0
      const suffix = total === 1 ? 'job' : 'jobs'
      return this.isFiltered
        ? `${total.toLocaleString()} matching ${suffix}`
        : `${total.toLocaleString()} ${suffix}`
    },
    currentBreakPoint() {
      return this.$store.getters['app/currentBreakPoint']
    },
    isMobile() {
      return !['lg', 'xl'].includes(this.currentBreakPoint)
    },
  },
  watch: {
    currentPage() {
      this.fetchJobs()
      this.scrollListToTop()
    },
    jobStatus() {
      this.requestJobs()
    },
  },
  created() {
    // Typing on a phone should filter as you go instead of waiting for a blur.
    this.debouncedSearch = debounce(this.requestJobs, 400)
    this.init()
  },
  beforeDestroy() {
    this.debouncedSearch.cancel()
  },
  methods: {
    onMobileSearch(value) {
      this.searchInput = value
      this.debouncedSearch()
    },
    requestJobs() {
      if (this.currentPage === 1) {
        this.fetchJobs()
      } else {
        this.currentPage = 1
      }
    },
    clearFilters() {
      this.searchInput = ''
      this.jobStatus = ALL_STATUSES
      this.requestJobs()
    },
    scrollListToTop() {
      if (!this.isMobile) return
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    onRowSelected(items) {
      this.selected = items
    },
    fetchJobs() {
      this.$store
        .dispatch('jobs/fetchJobs', {
          pageIndex: this.pageIndex,
          status: this.jobStatus,
          searchTerm: this.searchInput.trim(),
        })
        .catch(error => {
          if (error.response?.status === 403) {
            this.hasPermissions = false
            return
          }
          this.errorToast('Something went wrong')
        })
    },
    fetchJobStatuses() {
      this.$store.dispatch('jobs/fetchJobStatuses').catch(() => {
        this.errorToast('Something went wrong')
      })
    },
    init() {
      this.fetchJobStatuses()
      this.fetchJobs()
      if (this.$route.params.toast) {
        const { toast } = this.$route.params
        this.generalToast(toast.message, toast.title, toast.variant)
      }
    },
    cancelJob(job) {
      if (!job || !job.isCancelable) return
      this.confirmDelete(
        'Cancel Job',
        'Are you sure you want to cancel this job?',
        'Confirm',
      ).then(ok => {
        if (ok) {
          this.$store
            .dispatch('jobs/cancelJob', job.name)
            .then(() => {
              this.successToast('Job canceled successfully')
              this.fetchJobs()
            })
            .catch(error => {
              const message = error.response?.data?.Message
              this.errorToast(message || 'An error occurred.')
            })
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/variables/variables';

.searchInputGroup {
  width: 250px;
}
.searchInput {
  margin-right: 0;
}

.jobs-page {
  &__mobile-actions {
    margin-bottom: 4px;

    ::v-deep .btn {
      height: 44px;
      margin-right: 0;
    }
  }

  &__mobile-list {
    padding-top: 12px;
  }

  &__count {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
    font-size: 12px;
    font-weight: 500;
    color: $blue-800;
  }
}
</style>
